import React from "react";
import DataTable from "components/common/table/DataTable";
import { useTranslation } from "react-i18next";
import {
  useDisclosure,
  Input,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Switch,
  Selection,
} from "@heroui/react";
import { NavLink } from "react-router-dom";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import AddCustomerGroupModal from "components/common/modal/AddCustomerGroupModal";
import CustomerGroupSettingsSecondaryActions from "./CustomerGroupSettingsSecondaryActions";

import { LuChevronDown } from "react-icons/lu";
import { IoSearchSharp } from "react-icons/io5";
import { MdOutlineAutoMode } from "react-icons/md";
import { CgUnavailable } from "react-icons/cg";

const columns = [
  { name: "ID", uid: "customer_group_id", sortable: true, style: { width: "200px" } },
  { name: "Alias", uid: "customer_group_alias", sortable: true },
  { name: "Status", uid: "status_automation", sortable: true },
  { name: "Actions", uid: "actions", style: { width: "50px" } },
];

const statusAutomationOptions = [
  { name: "Automatic", uid: "auto" },
  { name: "Review", uid: "pending_review" },
];

type CustomerType = {
  customer_group_id: string;
  customer_group_alias: string;
  status_automation: string;
};

export default function CustomerGroupParams() {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();
  const axiosPrivate = useAxiosPrivate();
  const [filterValue, setFilterValue] = React.useState("");
  const [statusAutomationFilter, setStatusAutomationFilter] = React.useState<Selection>("all");

  const { data: apiResponse, isLoading, isError } = useQuery<{ data: CustomerType[], pagination: any }>({
    queryKey: ["CustomerGroupSettings"],
    queryFn: async () => {
      const response = await axiosPrivate.get(`/customers_group/settings?per_page=1000`);
      return response.data;
    },
    staleTime: 6000000,
    gcTime: 6000000,
  });

  const customersGroupSettingsData = React.useMemo(
    () => (apiResponse?.data ?? []).filter((item: any) => item.SK?.startsWith("SETTINGS#")),
    [apiResponse],
  );

  const filteredCustomers = React.useMemo(() => {
    let filtered = [...customersGroupSettingsData];

    if (filterValue) {
      filtered = filtered.filter(
        (customer) =>
          customer.customer_group_alias
            .toLowerCase()
            .includes(filterValue.toLowerCase()) ||
          customer.customer_group_id
            .toLowerCase()
            .includes(filterValue.toLowerCase()),
      );
    }

    if (
      statusAutomationFilter !== "all" &&
      Array.from(statusAutomationFilter).length !==
        statusAutomationOptions.length
    ) {
      filtered = filtered.filter((customer) =>
        Array.from(statusAutomationFilter).includes(customer.status_automation),
      );
    }

    return filtered;
  }, [filterValue, statusAutomationFilter, customersGroupSettingsData]);

  const classNames = React.useMemo(
    () => ({
      wrapper: ["max-h-[382px]"],
      table: ["border-separate", "border-spacing-y-2"],
      th: ["bg-transparent", "text-default-500", "border-b", "border-divider"],
      td: [
        "bg-white",
        "p-3",
        "group-data-[first=true]:first:before:rounded-none",
        "group-data-[first=true]:last:before:rounded-none",
        "group-data-[middle=true]:before:rounded-none",
        "group-data-[last=true]:first:before:rounded-none",
        "group-data-[last=true]:last:before:rounded-none",
      ],
      label: [
        "text-[10px] uppercase text-vistagrey/60 z-0 group-data-[filled=true]:text-vistagrey/60",
      ],
    }),
    [],
  );



  if (isLoading) {
    return <div>{t("Loading...")}</div>;
  }

  if (isError) {
    return <div>{t("Error loading data")}</div>;
  }

  const renderCell = (customer: CustomerType, columnKey: React.Key) => {
    const cellValue = customer[columnKey as keyof CustomerType];

    switch (columnKey) {
      case "status_automation":
        return (
          <Switch
            defaultSelected={customer.status_automation === "auto"}
            size="sm"
            color="primary"
            startContent={<MdOutlineAutoMode />}
            endContent={<CgUnavailable />}
            onChange={(e) =>
              handleStatusChange(customer.customer_group_id, e.target.checked)
            }
          >
            {t("mode_auto")}
          </Switch>
        );
      case "actions":
        return (
          <div className="relative flex justify-end items-center gap-2">
            <CustomerGroupSettingsSecondaryActions customer_group_id={customer.customer_group_id} />
          </div>
        );
      default:
        return (
          <NavLink to={`/customersgroup/settings/${customer.customer_group_id}`} className="flex items-center w-full h-full px-3 py-2">
            {cellValue}
          </NavLink>
        );
    }
  };

  const topContent = (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-3">
          <Input
            isClearable
            classNames={{
              base: "w-full min-w-[75%]",
              inputWrapper: [
                "border-0",
                "bg-white",
                "h-9",
                "min-h-unit-9",
                "hover:bg-white",
                "group-data-[hover=true]:bg-white",
                "group-data-[focus=true]:bg-white",
              ],
              input: "text-vistagrey",
            }}
            placeholder={t("search")}
            size="md"
            startContent={<IoSearchSharp className="text-default-300" />}
            value={filterValue}
            onClear={() => setFilterValue("")}
            onValueChange={setFilterValue}
          />
          <Dropdown>
            <DropdownTrigger>
              <Button
                endContent={<LuChevronDown className="text-small" />}
                size="sm"
                variant="flat"
                radius="full"
                className="h-9 bg-white text-vistagrey text-small"
              >
                {t("Status")}
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              disallowEmptySelection
              closeOnSelect={false}
              selectedKeys={statusAutomationFilter}
              selectionMode="multiple"
              onSelectionChange={setStatusAutomationFilter}
            >
              {statusAutomationOptions.map((status) => (
                <DropdownItem key={status.uid}>{t(status.name)}</DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
  const handleStatusChange = async (customer_group_id: string, newStatus: boolean) => {
    const data = {status_automation: newStatus ? "auto" : "pending_review"}
    try {
      await axiosPrivate.put(`/customers_group/settings/${customer_group_id}`, data);

      queryClient.setQueryData(["CustomerGroupSettings"], (oldData: any) => {
        if (oldData) {
          return {
            ...oldData,
            data: oldData.data.map((customer: CustomerType) =>
              customer.customer_group_id === customer_group_id
                ? { ...customer, status_automation: data.status_automation }
                : customer
            ),
          };
        }
        return oldData;
      });

    } catch (error) {
      console.error("Failed to update status", error);
    }
  };
  
  return (
    <>
      <div className="flex w-full justify-end">
        <Button
          onPress={onOpen}
          className="bg-vistagreen text-white rounded-md p-3 absolute -mt-16"
        >
          {t("add_customer_group_settings")}
        </Button>
      </div>
      <DataTable<CustomerType>
        columns={columns}
        data={filteredCustomers || []}
        renderCell={renderCell}
        keyExtractor={(customer) => customer.customer_group_id}
        initialSortDescriptor={{
          column: "customer_name",
          direction: "ascending",
        }}
        topContent={topContent}
        classNames={classNames}
      />
      <AddCustomerGroupModal
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
}
